import { useState } from 'react';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import { Button } from '@progress/kendo-react-buttons';
import { useKeycloak } from '../shared/auth/KeycloakProvider.tsx';
import { PageTitle } from './PageTitle.tsx';
import { Fieldset } from './form-components/Fieldset.tsx';
import { Label } from '@progress/kendo-react-labels';
import { restApiUrl } from '../shared/helpers/backendUrl';

export const ExportSignUpsPage: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useKeycloak();

  const handleExport = async () => {
    if (!startDate || !endDate) return;

    setIsLoading(true);
    setError(null);

    try {
      // Set endDate to end of day so all signups from that day are included
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);

      const url = restApiUrl(
        `/sign-ups/export?startDate=${startDate.toISOString()}&endDate=${endOfDay.toISOString()}`,
      );

      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) {
        throw new Error(`Export fehlgeschlagen (${response.status})`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'anmeldungen-export.xlsx';
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PageTitle title="Anmeldungen exportieren" />
      <div className="k-form k-form-md relative flex w-full flex-col rounded-md bg-white shadow lg:w-full">
        <Fieldset
          legend="Zeitraum"
          description="Wählen Sie den Zeitraum, für den Sie die Anmeldungen als Excel-Datei exportieren möchten."
        >
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <div className="k-form-field">
              <Label editorId="export-start-date">Von</Label>
              <DatePicker
                id="export-start-date"
                format="dd.MM.yyyy"
                value={startDate}
                onChange={(e) => setStartDate(e.value)}
                max={endDate ?? undefined}
              />
            </div>
            <div className="k-form-field">
              <Label editorId="export-end-date">Bis</Label>
              <DatePicker
                id="export-end-date"
                format="dd.MM.yyyy"
                value={endDate}
                onChange={(e) => setEndDate(e.value)}
                min={startDate ?? undefined}
              />
            </div>
          </div>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </Fieldset>

        <div className="k-form-buttons !mt-0 justify-end rounded-md bg-gray-50 !px-8 !py-3 !text-right">
          <Button
            themeColor="primary"
            className="!shadow-sm [&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
            disabled={!startDate || !endDate || isLoading}
            onClick={handleExport}
          >
            {isLoading ? 'Exportieren...' : 'Excel exportieren'}
          </Button>
        </div>
      </div>
    </>
  );
};
